<script>
import { preventDefault } from 'svelte/legacy';
import {
    fade
} from 'svelte/transition';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    toast
} from '@zerodevx/svelte-toast';
import EventGroup from '@cclib/classes/eventGroup.js';
import convertToPlainText from '@cclib/functions/quill/convertToPlainText.js';
import * as m from "@app/paraglide/messages.js";

let selectedGroups = $state([]);
let primaryGroup = $state("");
let matchBy = $state("index");
let mergeNotes = $state(true);
let mergeReply = $state(true);
let mergeTags = $state(true);
let mergeSpeakers = $state(true);
let mergeEventGroupMetadata = $state(true);
let working = $state(false);

// Auto-set primary group to first selected when selection changes
$effect(() => {
    if (selectedGroups.length > 0 && !selectedGroups.includes(primaryGroup)) {
        primaryGroup = selectedGroups[0];
    }
});

/**
 * Deduplicate an array of objects by a property, or strings by normalized value.
 */
function deduplicateArray(arr, prop) {
    if (!arr || !arr.length) return [];
    const seen = new Map();
    return arr.filter(item => {
        if (item === null || item === undefined) return false;
        if (typeof item !== 'object' || item === null) {
            const key = String(item).trim().toLowerCase();
            if (seen.has(key)) return false;
            seen.set(key, true);
            return true;
        }
        const key = item[prop] || item.id || JSON.stringify(item);
        if (seen.has(key)) return false;
        seen.set(key, true);
        return true;
    });
}

/**
 * Find matching event in source group based on selected strategy.
 */
function findMatch(baseEvent, sourceEvents, strategy) {
    if (strategy === "index") {
        return null; // handled directly by index in caller
    }

    if (strategy === "time") {
        let bestMatch = null;
        let bestDiff = Infinity;
        for (const srcEvent of sourceEvents) {
            const diff = Math.abs(srcEvent.start - baseEvent.start);
            if (diff < 0.1 && diff < bestDiff) {
                bestDiff = diff;
                bestMatch = srcEvent;
            }
        }
        return bestMatch;
    }

    if (strategy === "text") {
        const baseText = convertToPlainText(baseEvent.text || "", " ");
        for (const srcEvent of sourceEvents) {
            const srcText = convertToPlainText(srcEvent.text || "", " ");
            if (baseText && srcText && baseText === srcText) {
                return srcEvent;
            }
        }
        return null;
    }

    return null;
}

/**
 * Merge metadata fields from a source event into a target event.
 */
function mergeEventMetadata(targetEvent, sourceEvent) {
    if (mergeNotes && sourceEvent.notes) {
        if (targetEvent.notes) {
            // Only concatenate if source notes differ
            if (targetEvent.notes !== sourceEvent.notes) {
                targetEvent.notes += "\n" + sourceEvent.notes;
            }
        } else {
            targetEvent.notes = sourceEvent.notes;
        }
    }

    if (mergeReply && sourceEvent.reply) {
        if (targetEvent.reply) {
            if (targetEvent.reply !== sourceEvent.reply) {
                targetEvent.reply += "\n" + sourceEvent.reply;
            }
        } else {
            targetEvent.reply = sourceEvent.reply;
        }
    }

    if (mergeTags && sourceEvent.tags?.length) {
        targetEvent.tags = deduplicateArray([
            ...(targetEvent.tags || []),
            ...sourceEvent.tags
        ]);
    }

    if (mergeSpeakers && sourceEvent.speakers?.length) {
        targetEvent.speakers = deduplicateArray([
            ...(targetEvent.speakers || []),
            ...sourceEvent.speakers
        ], 'id');
    }

    // Merge event-level metadata array
    if (sourceEvent.metadata?.length) {
        targetEvent.metadata = deduplicateArray([
            ...(targetEvent.metadata || []),
            ...sourceEvent.metadata
        ], 'prop');
    }

    // Copy event positioning from the matched source event when present.
    if (sourceEvent.xPos !== undefined && sourceEvent.xPos !== null) {
        targetEvent.xPos = sourceEvent.xPos;
    }

    if (sourceEvent.yPos !== undefined && sourceEvent.yPos !== null) {
        targetEvent.yPos = sourceEvent.yPos;
    }
}

function mergeMetadata() {
    if (working || selectedGroups.length < 2) return;
    working = true;

    try {
        // Get selected groups in order
        const groups = eventGroupState.filter(g => selectedGroups.includes(g.id));
        if (groups.length < 2) {
            toast.push(m.metadata_merge_minimum_warning(), { classes: ['toast-warning'] });
            return;
        }

        const baseGroup = primaryGroup
            ? eventGroupState.find(g => g.id === primaryGroup)
            : groups[0];

        if (!baseGroup) {
            toast.push(m.metadata_merge_failed({ error: "Primary group not found" }), { classes: ['toast-danger'] });
            return;
        }

        const sourceGroups = groups.filter(g => g.id !== baseGroup.id);

        // Deep clone the base group
        let mergedGroup = JSON.parse(JSON.stringify(baseGroup));
        mergedGroup.name = baseGroup.name + " (Metadata Merged)";

        // Merge events from each source group
        for (const sourceGroup of sourceGroups) {
            for (let i = 0; i < mergedGroup.events.length; i++) {
                let matchedEvent = null;

                if (matchBy === "index") {
                    if (i < sourceGroup.events.length) {
                        matchedEvent = sourceGroup.events[i];
                    }
                } else {
                    matchedEvent = findMatch(mergedGroup.events[i], sourceGroup.events, matchBy);
                }

                if (matchedEvent) {
                    mergeEventMetadata(mergedGroup.events[i], matchedEvent);
                }
            }

            // Merge Event Group level metadata
            if (mergeEventGroupMetadata) {
                if (sourceGroup.metadata?.length) {
                    mergedGroup.metadata = deduplicateArray([
                        ...(mergedGroup.metadata || []),
                        ...sourceGroup.metadata
                    ], 'prop');
                }

                if (mergeSpeakers && sourceGroup.speakers?.length) {
                    mergedGroup.speakers = deduplicateArray([
                        ...(mergedGroup.speakers || []),
                        ...sourceGroup.speakers
                    ], 'id');
                }
            }
        }

        // Create new EventGroup with merged data
        eventGroupState.push({...(new EventGroup(mergedGroup))});
        projectState.selected = eventGroupState.length - 1;

        insertHistoryAction({
            name: "metadata merge",
            eventGroup: projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }],
        });

        toast.push(m.metadata_merge_success(), { classes: ['toast-success'] });
    } catch (err) {
        console.error("Error merging metadata:", err);
        toast.push(m.metadata_merge_failed({ error: err.message }), { classes: ['toast-danger'] });
    } finally {
        working = false;
        hideModal();
    }
}
</script>

<div transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'metadataMerge' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="metadataMergeTitle"
    aria-modal="true"
    tabindex="-1" 
    id="MetadataMergeModal">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="metadataMergeTitle">{m.metadata_merge_title()}</h4>
                <button type="button" 
                    class="btn-close" 
                    id="btnCloseMetadataMerge"
                    aria-label={m.metadata_merge_close_aria_label()} 
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <form onsubmit={preventDefault(mergeMetadata)}>
                    <!-- Event Group Selection -->
                    <div class="mb-3">
                        <label class="form-label" for="metadataMergeGroups">{m.metadata_merge_event_groups_label()}</label>
                        <select multiple class="form-select" id="metadataMergeGroups" bind:value={selectedGroups} size="5">
                            {#each eventGroupState as eventGroup}
                                <option value={eventGroup.id}>{eventGroup.name}</option>
                            {/each}
                        </select>
                        {#if selectedGroups.length < 2}
                            <div class="form-text text-warning">
                                <i class="bi bi-exclamation-triangle" aria-hidden="true"></i> {m.metadata_merge_minimum_warning()}
                            </div>
                        {/if}
                    </div>

                    <!-- Primary Event Group -->
                    {#if selectedGroups.length >= 2}
                    <div class="mb-3 fade-in">
                        <label class="form-label" for="metadataMergePrimary">{m.metadata_merge_primary_group_label()}</label>
                        <select class="form-select" id="metadataMergePrimary" bind:value={primaryGroup}>
                            {#each eventGroupState.filter(g => selectedGroups.includes(g.id)) as eventGroup}
                                <option value={eventGroup.id}>{eventGroup.name}</option>
                            {/each}
                        </select>
                        <div class="form-text">{m.metadata_merge_primary_group_help()}</div>
                    </div>
                    {/if}

                    <!-- Properties to Merge -->
                    <div class="mb-3">
                        <span class="form-label d-block">{m.metadata_merge_properties_label()}</span>
                        <div class="form-check form-switch mb-2">
                            <input type="checkbox" class="form-check-input" id="mergeNotes" bind:checked={mergeNotes}>
                            <label class="form-check-label" for="mergeNotes">{m.metadata_merge_notes()}</label>
                        </div>
                        <div class="form-check form-switch mb-2">
                            <input type="checkbox" class="form-check-input" id="mergeReply" bind:checked={mergeReply}>
                            <label class="form-check-label" for="mergeReply">{m.metadata_merge_reply()}</label>
                        </div>
                        <div class="form-check form-switch mb-2">
                            <input type="checkbox" class="form-check-input" id="mergeTags" bind:checked={mergeTags}>
                            <label class="form-check-label" for="mergeTags">{m.metadata_merge_tags()}</label>
                        </div>
                        <div class="form-check form-switch mb-2">
                            <input type="checkbox" class="form-check-input" id="mergeSpeakers" bind:checked={mergeSpeakers}>
                            <label class="form-check-label" for="mergeSpeakers">{m.metadata_merge_speakers()}</label>
                        </div>
                        <div class="form-check form-switch mb-2">
                            <input type="checkbox" class="form-check-input" id="mergeEgMetadata" bind:checked={mergeEventGroupMetadata}>
                            <label class="form-check-label" for="mergeEgMetadata">{m.metadata_merge_event_group_metadata()}</label>
                        </div>
                    </div>

                    <!-- Match Strategy -->
                    <div class="mb-3">
                        <label class="form-label" for="metadataMergeMatchBy">{m.metadata_merge_match_by_label()}</label>
                        <select class="form-select" id="metadataMergeMatchBy" bind:value={matchBy}>
                            <option value="index">{m.metadata_merge_match_by_index()}</option>
                            <option value="time">{m.metadata_merge_match_by_time()}</option>
                            <option value="text">{m.metadata_merge_match_by_text()}</option>
                        </select>
                    </div>

                    <!-- Merge Button -->
                    <div class="d-grid">
                        <button type="submit" 
                            class="btn btn-primary" 
                            disabled={selectedGroups.length < 2 || working}>
                            {#if working}
                                <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            {/if}
                            {m.metadata_merge_button()}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</div>

<style>
    .fade-in {
        animation: fadeIn 0.2s ease-in;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
</style>
